import { ArpMedia, ArpMediaType, MAX_SAMPLES_PER_PACK, SamplePack } from '@arpeggi/shared'
import { useSnackbar } from 'notistack'
import { useMemo, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { styled } from '../../../lib/stitches'
import { useArpMedia } from '../../../queries/arpMedia'
import {
  useCreateSamplePack,
  useDeleteSamplePack,
  useUpdateSamplePack
} from '../../../queries/samplePacks'
import Button from '../../core/Button/Button'
import { Box } from '../../core/Components'
import { FieldErrorLabel } from '../../core/FieldErrorLabel'
import { FieldLabel } from '../../core/FieldLabel'
import { Flex } from '../../core/Flex'
import Text from '../../core/Text/Text'
import { TextArea } from '../../core/TextArea'
import { TextInput } from '../../core/TextInput'
import { InternalEndpoint } from '../../Internal'
import { ArpMediaMultiSelect } from '../../internal/components/ArpMediaMultiSelect'
import { ArpMediaSelect } from '../../internal/components/ArpMediaSelect'
import { FileUpload } from '../../internal/components/common'
import { UserSelect } from '../../internal/components/UserSelect'
import { Spinner } from '../../platform/explore/common/utils'
import { ArpIdsInput } from './ArpIdsInput'

interface Props {
  samplePack?: SamplePack
  sounds?: ArpMedia[]
}

interface FormFields {
  title: string
  description: string
  userId: number
  soundIds: number[]
  project?: null | { name: string; url: string }
  coverArpMediaId?: null | number
}

const StyledForm = styled('form', {
  display: 'flex',
  gap: '8px',
  flexDirection: 'column',
  maxWidth: '1280px'
})

export const SamplePackForm: React.FC<Props> = ({ samplePack, sounds = [] }) => {
  const [file, setFile] = useState<File>(undefined)
  const [fileError, setFileError] = useState<string>(undefined)
  const [arpIdsInput, setArpIdsInput] = useState<number[]>([])
  const { enqueueSnackbar } = useSnackbar()

  const {
    control,
    handleSubmit,
    formState: { errors, submitCount },
    setValue,
    watch
  } = useForm<FormFields>({
    mode: 'onBlur',
    defaultValues: {
      title: samplePack?.title,
      description: samplePack?.description,
      userId: samplePack?.userId,
      soundIds: sounds.map((sound) => sound.id) || [],
      project: samplePack?.project,
      coverArpMediaId: samplePack?.coverArpMediaId
    }
  })
  const coverArpMediaId = samplePack?.coverArpMediaId
  const coverArpQuery = useArpMedia({
    id: coverArpMediaId,
    options: { enabled: !!coverArpMediaId }
  })
  const updateSamplePack = useUpdateSamplePack()
  const createSamplePack = useCreateSamplePack()
  const deleteSamplePack = useDeleteSamplePack()
  const navigate = useNavigate()

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    if (!file && !isUpdateMode) {
      setFileError('Image is required')
      return
    }

    if (file && file.size / 1024 > 1024 * 6) {
      setFileError('Image size cannot exceed 6MB')
      return
    }

    const soundsCount = data.soundIds.length

    if (!soundsCount) {
      return
    }

    if (soundsCount > MAX_SAMPLES_PER_PACK) {
      return enqueueSnackbar({
        message: `Media count cannot exceed 300 (currently ${soundsCount}).`,
        variant: 'modalError'
      })
    }

    const dto = { ...data, coverImage: file }

    if (isUpdateMode) {
      updateSamplePack.mutate(
        {
          id: samplePack.id,
          dto: {
            ...dto,
            project: dto.project.name && dto.project.url ? dto.project : null,
            coverArpMediaId: dto.coverArpMediaId || null
          }
        },
        {
          onSuccess: () => {
            enqueueSnackbar('Sample pack updated!')
          }
        }
      )
    } else {
      createSamplePack.mutate(
        { ...dto, soundIds: Array.from(new Set([...data.soundIds, ...arpIdsInput])) },
        {
          onSuccess: () => {
            navigate(`/${InternalEndpoint}/sample-packs`)
            enqueueSnackbar('Sample pack created!')
          }
        }
      )
    }
  }

  const handelAddingSoundIds = async (ids: number[]) => {
    await updateSamplePack.mutateAsync(
      {
        id: samplePack.id,
        dto: {
          soundIds: Array.from(new Set([...sounds.map((x) => x.id), ...ids]))
        }
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Arp media added!')
        }
      }
    )
  }

  const watchUserId = watch('userId')
  const watchSampleIds = watch('soundIds')
  const watchProjectName = watch('project.name')
  const watchProjectUrl = watch('project.url')
  const isUpdateMode = samplePack !== undefined
  const imagePreviewUrl = useMemo(() => {
    return file ? URL.createObjectURL(file) : samplePack?.coverImage.url
  }, [file, samplePack?.coverImage])
  const isMultiSelectError = useMemo(
    () => watchSampleIds.length + arpIdsInput.length === 0 && submitCount > 0,
    [watchSampleIds, arpIdsInput, submitCount]
  )

  return (
    <>
      <Text size="24" css={{ marginBottom: '$16' }}>
        {isUpdateMode ? 'Update Sample Pack' : 'Create Sample Pack'}
      </Text>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" width="full">
          <FieldLabel htmlFor="title">Title</FieldLabel>
          <Controller
            name="title"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Required'
              },
              maxLength: {
                value: 40,
                message: 'At most 40 characters'
              }
            }}
            render={({ field }) => <TextInput id="title" placeholder="Title" {...field} />}
          />
          <FieldErrorLabel>{errors.title?.message}</FieldErrorLabel>
        </Flex>
        <Flex direction="column" width="full">
          <FieldLabel htmlFor="description">Description</FieldLabel>
          <Controller
            name="description"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Required'
              },
              maxLength: {
                value: 1000,
                message: 'At most 1000 characters'
              }
            }}
            render={({ field }) => (
              <TextArea id="description" placeholder="Description" {...field} />
            )}
          />
          <FieldErrorLabel>{errors.description?.message}</FieldErrorLabel>
        </Flex>
        <Flex direction="column" width="full">
          <FileUpload
            fieldName="image"
            label="Image"
            onChange={(e) => {
              setFile(e.target.files[0])
              setFileError(undefined)
            }}
            errorMessage={fileError}
            imagePreviewUrl={imagePreviewUrl}
            acceptedFiles={['image/jpeg', 'image/png', 'image/jpg', 'image/gif']}
          />
        </Flex>
        <Box
          css={{
            backgroundColor: '$gray02',
            padding: '$16',
            borderRadius: '$10',
            marginBottom: '$10'
          }}
        >
          <Text size="16">Project (optional)</Text>
          <Flex
            direction="column"
            width="full"
            css={{ gap: 0, '@mobile': { flexDirection: 'row', gap: '16px' } }}
          >
            <Flex direction="column" css={{ flex: 1 }}>
              <FieldLabel htmlFor="project.name">Project name</FieldLabel>
              <Controller
                name="project.name"
                control={control}
                rules={{
                  required: {
                    value: !!watchProjectUrl,
                    message: 'Required'
                  },
                  maxLength: {
                    value: 50,
                    message: 'At most 50 characters'
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <TextInput id="project.name" placeholder="Project name" {...field} />
                    <FieldErrorLabel>{error?.message}</FieldErrorLabel>
                  </>
                )}
              />
            </Flex>
            <Flex direction="column" css={{ flex: 1 }}>
              <FieldLabel htmlFor="project.url">Project URL</FieldLabel>
              <Controller
                name="project.url"
                control={control}
                rules={{
                  required: {
                    value: !!watchProjectName,
                    message: 'Required'
                  },
                  pattern: {
                    value:
                      /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()!@:%_\+.~#?&\/\/=]*)/,
                    message: 'Url is invalid'
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <TextInput id="project.url" placeholder="Project URL" {...field} />
                    <FieldErrorLabel>{error?.message}</FieldErrorLabel>
                  </>
                )}
              />
            </Flex>
          </Flex>
          <Text size="16">Arp Media Cover (optional)</Text>
          {(!coverArpQuery.isLoading || !coverArpMediaId) && (
            <ArpMediaSelect
              onChange={(arpMedia) => setValue('coverArpMediaId', arpMedia?.id || null)}
              defaultArpMedia={coverArpQuery?.data}
            />
          )}
        </Box>
        <UserSelect
          defaultUser={samplePack?.user}
          onChange={(user) => setValue('userId', user?.id)}
          errorMessage={!watchUserId && submitCount > 0 && 'Must select a user'}
        />
        <Box css={{ maxWidth: '680px' }}>
          <ArpIdsInput
            onSubmit={isUpdateMode ? handelAddingSoundIds : undefined}
            onChange={isUpdateMode ? undefined : (arpIds) => setArpIdsInput(arpIds)}
            isDisabled={updateSamplePack.isLoading}
          />
          <Flex
            css={{ justifyContent: 'center', width: '400px', maxWidth: '100%', marginTop: '16px' }}
          >
            <Text size="16">OR</Text>
          </Flex>
          <ArpMediaMultiSelect
            types={[ArpMediaType.SAMPLE, ArpMediaType.STEM]}
            onChange={(samples) =>
              setValue(
                'soundIds',
                samples.map((sample) => sample.id)
              )
            }
            defaultSelectedArp={sounds}
            errorMessage={isMultiSelectError && 'Must select at least one sample'}
          />
        </Box>
        <Button
          type={'dark'}
          big={true}
          rounded={true}
          disabled={updateSamplePack.isLoading}
          onClick={handleSubmit(onSubmit)}
          css={{ maxWidth: '300px', marginTop: '$16' }}
        >
          {updateSamplePack.isLoading ? (
            <Spinner />
          ) : (
            <Text type={'primary_inverted'} size={'14'}>
              {isUpdateMode ? 'Save changes' : 'Save'}
            </Text>
          )}
        </Button>
      </StyledForm>
      {isUpdateMode && (
        <Box
          css={{
            borderColor: '$crimson05',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderRadius: '$10',
            padding: '$16',
            marginTop: '$32',
            maxWidth: '1280px'
          }}
        >
          <Text size="20">Danger Zone</Text>
          <Button
            type="dark"
            big
            css={{ backgroundColor: '$crimson05', marginTop: '16px' }}
            onClick={() =>
              deleteSamplePack.mutate(samplePack.id, {
                onSuccess: () => {
                  enqueueSnackbar('Sample pack deleted.')
                  navigate(`/${InternalEndpoint}/sample-packs`)
                }
              })
            }
            disabled={deleteSamplePack.isLoading}
          >
            Delete Sample Pack
          </Button>
        </Box>
      )}
    </>
  )
}
